import React, { memo } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { useField } from 'formik';

// constants
import { PhoneNumberTypes } from 'enums/onboarding/crm';

// components
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import PhoneTypeSelect from '../../../FormComponents/SelectInputs/CRM/PhoneTypeSelect';
import PrimaryStatusText from '../../../../Typography/PrimaryStatusText';
import HideIfDisabledForm from '../../../HideIfDisabledForm';
import {
  FormInputPhoneNumber,
  FormField,
  DeleteButton,
  Col,
  Row,
  Tooltip,
} from '@ui';

interface IProps {
  fieldName: string;
  canVerify?: boolean;
  removeCallback?: () => void;
}

export interface FormValuesModel {
  type: PhoneNumberTypes | null;
  value: string;
  primary: {
    status: boolean;
    canEdit: boolean;
  };
  disabledPhoneNumberFieldTooltipText?: string;
}

const PhoneNumberFormField = memo(({ title, ...rest }: { title: string }) => {
  return (
    <Tooltip title={title}>
      <div>
        <FormInputPhoneNumber {...rest} />
      </div>
    </Tooltip>
  );
});

const PhoneNumberForm = ({ fieldName, removeCallback }: IProps) => {
  const { t } = useTranslation('onboarding');
  const [field] = useField<FormValuesModel>(fieldName);

  const primary = field.value.primary;
  const gridSizes = { xl: 8, lg: 8, md: 8, sm: 24, xs: 24 };

  return (
    <Row gutter={[16, 0]}>
      <Col {...gridSizes}>
        <FormField
          label={t(
            'applicant_information.form_fields.individual.phone_number.type',
          )}
          name={`${fieldName}.type`}
          component={PhoneTypeSelect}
          disabled={!primary.canEdit}
        />
      </Col>

      <Col flex="1 1">
        <FormField
          label={t(
            'applicant_information.form_fields.individual.phone_number.phone_number',
          )}
          name={`${fieldName}.value`}
          component={PhoneNumberFormField}
          disabled={!primary.canEdit}
          additionalProps={{
            title:
              !primary.canEdit &&
              field.value.disabledPhoneNumberFieldTooltipText
                ? field.value.disabledPhoneNumberFieldTooltipText
                : null,
          }}
        />
      </Col>

      <DivAlignCenter>
        <StyledHelperCol>
          {primary.status ? (
            <PrimaryStatusText />
          ) : (
            removeCallback && (
              <HideIfDisabledForm hideIfSubmitting>
                <DeleteButton onClick={removeCallback} />
              </HideIfDisabledForm>
            )
          )}
        </StyledHelperCol>
      </DivAlignCenter>
    </Row>
  );
};

const StyledHelperCol = styled(Col)`
  display: flex;
  align-items: flex-end;
`;

export default PhoneNumberForm;
